/**
 * For Investors Marketing Page — orchardoffers.com/investors.
 *
 * Investor-facing companion to the agent-facing home page (/).
 *
 * Sections: hero → how it works → value props → markets → CTA → footer.
 */

import type { MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { Text } from '@/common/components/text'
import { MARKETS, ACTIVE_MARKET_COUNT } from '@/common/markets'
import * as styles from './investors.css'

export const Route = createFileRoute({
  component: InvestorsPage,
})

function InvestorsPage() {
  const { isAuthenticated } = useAuth()

  const scrollToFeatures = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToFeaturesButton = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Offers"
          className={styles.logo}
        />
        <nav className={styles.nav}>
          <Link to="/" className={styles.navLink}>
            For Agents
          </Link>
          <a href="#features" className={styles.navLink} onClick={scrollToFeatures}>
            How it works
          </a>
          {isAuthenticated ? (
            <Link to="/properties">
              <Button variant="primary">
                <Icon name="dashboard" size={18} />
                Go to Dashboard
              </Button>
            </Link>
          ) : (
            <div className={styles.navButtons}>
              <Link to="/request-account" className={styles.joinButton}>
                <Button variant="outline">Join the Marketplace</Button>
              </Link>
              <Link to="/login">
                <Button variant="primary">Sign In</Button>
              </Link>
            </div>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <Text as="h1" variant="h1" className={styles.heroTitle}>
              Exclusive access to off-market investment properties
            </Text>
            <Text variant="bodyLg" className={styles.heroSubtitle}>
              Orchard Offers connects vetted investors with premium properties
              before they hit the MLS. Get first access to deals others never see.
            </Text>
            <div className={styles.heroCta}>
              {isAuthenticated ? (
                <Link to="/properties">
                  <Button variant="primary" size="lg">
                    <Icon name="dashboard" size={20} />
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="primary" size="lg">
                    Sign In
                  </Button>
                </Link>
              )}
              <Button variant="outline" size="lg" onClick={scrollToFeaturesButton}>
                Learn More
              </Button>
            </div>
            {!isAuthenticated && (
              <Text variant="bodySm" className={styles.heroRequestLink}>
                New investor?{' '}
                <Link to="/request-account" className={styles.heroRequestLinkAnchor}>
                  Join the marketplace
                </Link>
              </Text>
            )}
          </div>
          <div className={styles.heroImageWrapper}>
            <img
              src="https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_2/w_500/web-assets/homepage/localized-homes/austin-tx.jpg"
              srcSet={[
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_1/w_370/web-assets/homepage/localized-homes/austin-tx.jpg 370w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_1/w_500/web-assets/homepage/localized-homes/austin-tx.jpg 500w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_2/w_370/web-assets/homepage/localized-homes/austin-tx.jpg 740w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_2/w_500/web-assets/homepage/localized-homes/austin-tx.jpg 1000w',
                'https://res.cloudinary.com/dmxgostd9/image/upload/f_auto,q_auto/dpr_3/w_500/web-assets/homepage/localized-homes/austin-tx.jpg 1500w',
              ].join(', ')}
              sizes="(max-width: 768px) 100vw, 500px"
              alt="Modern home exterior"
              className={styles.heroImage}
            />
          </div>
        </div>
      </section>

      {/* Wave Divider */}
      <div className={styles.waveDivider}>
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className={styles.waveSvg}
        >
          <path
            d="M0 80L1440 80L1440 20C1320 40 1200 60 1080 55C960 50 840 25 720 20C600 15 480 30 360 40C240 50 120 55 60 57.5L0 60L0 80Z"
            fill="white"
          />
        </svg>
      </div>

      {/* How It Works */}
      <section className={styles.howItWorks} id="features">
        <div className={styles.howItWorksInner}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>How it works</Text>
            <Text variant="bodyLg" align="center" className={styles.sectionSubtitle}>
              Three simple steps to start investing in off-market properties.
            </Text>
          </div>
          <div className={styles.stepsGrid}>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>1</div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.stepTitle}>Set Your Criteria</Text>
              <Text variant="body" className={styles.stepDescription}>
                Define your buybox with location, price range, property type,
                and other preferences to filter properties that fit your strategy.
              </Text>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>2</div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.stepTitle}>Browse Properties</Text>
              <Text variant="body" className={styles.stepDescription}>
                Access exclusive off-market listings with complete property data
                including ARV, repair estimates, and market analysis.
              </Text>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>3</div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.stepTitle}>Make Offers</Text>
              <Text variant="body" className={styles.stepDescription}>
                Submit offers directly through the platform. Work with Orchard
                agents to close quickly and efficiently.
              </Text>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className={styles.valueProps}>
        <div className={styles.valuePropsInner}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>
              Why investors choose Orchard Offers
            </Text>
            <Text variant="bodyLg" align="center" className={styles.sectionSubtitle}>
              Institutional-grade tools and access designed for individual
              investors.
            </Text>
          </div>
          <div className={styles.valueGrid}>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>
                <Icon name="shield" size={48} />
              </div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.valueTitle}>Exclusive Access</Text>
              <Text variant="body" className={styles.valueDescription}>
                Properties vetted and available only to approved investors. No
                bidding wars, no public listings.
              </Text>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>
                <Icon name="tune" size={48} />
              </div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.valueTitle}>Buybox Filtering</Text>
              <Text variant="body" className={styles.valueDescription}>
                Define your investment criteria once. Filter properties by
                location, price, and strategy to find what you're looking for.
              </Text>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>
                <Icon name="notifications_active" size={48} />
              </div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.valueTitle}>Instant Alerts</Text>
              <Text variant="body" className={styles.valueDescription}>
                Receive notifications when new properties hit the market.
                Never miss a deal again.
              </Text>
            </div>
            <div className={styles.valueItem}>
              <div className={styles.valueIcon}>
                <Icon name="handshake" size={48} />
              </div>
              <Text as="h3" variant="bodyLg" weight="bold" className={styles.valueTitle}>Dedicated Support</Text>
              <Text variant="body" className={styles.valueDescription}>
                Work directly with Orchard agents who know the local market and
                can help you close quickly.
              </Text>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Divider - lightBirch to white */}
      <div className={styles.waveDividerBirch}>
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className={styles.waveSvg}
        >
          <path
            d="M0 80L1440 80L1440 25C1380 35 1260 50 1140 45C1020 40 900 20 780 25C660 30 540 50 420 45C300 40 180 30 60 35L0 40L0 80Z"
            fill="white"
          />
        </svg>
      </div>

      {/* Markets Section */}
      <section className={styles.marketsSection}>
        <div className={styles.marketsInner}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="h2" align="center" className={styles.sectionTitle}>Markets we serve</Text>
            <Text variant="bodyLg" align="center" className={styles.sectionSubtitle}>
              Access off-market properties across {ACTIVE_MARKET_COUNT} growing markets.
            </Text>
          </div>

          <div className={styles.marketsLayout}>
            {/* US Map with markers */}
            <div className={styles.mapContainer}>
              <img
                src="/images/us-map.svg"
                alt="US map showing Orchard Offers markets"
                className={styles.mapImage}
              />
              {MARKETS.map((market) => (
                <div
                  key={`marker-${market.city}-${market.state}`}
                  className={market.active ? styles.mapMarker : styles.mapMarkerInactive}
                  style={{ left: `${market.x}%`, top: `${market.y}%` }}
                  title={`${market.city}, ${market.state}${market.active ? '' : ' (coming soon)'}`}
                />
              ))}
            </div>

            {/* Market lists */}
            <div className={styles.marketsListContainer}>
              <div className={styles.marketsList}>
                <Text as="h3" variant="label" className={styles.marketsListTitle}>Active Markets</Text>
                {MARKETS.filter((m) => m.active).map((market) => (
                  <Text key={`${market.city}-${market.state}`} variant="bodySm" className={styles.marketItem}>
                    {market.city}, {market.state}
                  </Text>
                ))}
              </div>
              <div className={styles.marketsList}>
                <Text as="h3" variant="label" color="muted" className={styles.marketsListTitleMuted}>Coming Soon</Text>
                {MARKETS.filter((m) => !m.active).map((market) => (
                  <Text key={`${market.city}-${market.state}`} variant="bodySm" color="muted" className={styles.marketItemMuted}>
                    {market.city}, {market.state}
                  </Text>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Divider - white to skyLight */}
      <div className={styles.waveDividerWhite}>
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className={styles.waveSvg}
        >
          <path
            d="M0 80L1440 80L1440 35C1350 20 1260 40 1140 50C1020 60 900 45 780 35C660 25 540 35 420 45C300 55 180 45 60 40L0 35L0 80Z"
            fill="#EDFBFD"
          />
        </svg>
      </div>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <Text as="h2" variant="h2" align="center" className={styles.ctaTitle}>
            Ready to see exclusive properties?
          </Text>
          <Text variant="bodyLg" align="center" className={styles.ctaSubtitle}>
            {isAuthenticated
              ? 'Head to your dashboard to browse off-market investment opportunities.'
              : 'Sign in to browse off-market investment opportunities.'}
          </Text>
          {isAuthenticated ? (
            <Link to="/properties">
              <Button variant="primary" size="lg">
                <Icon name="dashboard" size={20} />
                Go to Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="primary" size="lg">
                  Sign In
                </Button>
              </Link>
              <Text variant="bodySm" className={styles.ctaRequestLink}>
                New investor?{' '}
                <Link to="/request-account" className={styles.ctaRequestLinkAnchor}>
                  Join the marketplace
                </Link>
              </Text>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text variant="h4" align="center" className={styles.footerContactHeading}>Questions?</Text>
        <Text variant="bodyLg" align="center" className={styles.footerContactSubheading}>We're here to help.</Text>
        <a href="mailto:support@orchardoffers.com" className={styles.footerContactLink}>
          support@orchardoffers.com
        </a>
      </footer>
    </div>
  )
}
